import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AlertTriangle, CreditCard } from "lucide-react";
import { useAuth } from "@/providers/AuthProvider";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from "react-i18next";

type OrgAdmin = { first_name: string | null; last_name: string | null; email: string | null };

export const SubscriptionBlocker = () => {
	const { subscriptionIssue, isInGracePeriod, isSuperAdmin, isAdmin } = useAuth();
	const { t } = useTranslation();
	const location = useLocation();
	const navigate = useNavigate();
	const [orgAdmins, setOrgAdmins] = useState<OrgAdmin[]>([]);

	useEffect(() => {
		if (!subscriptionIssue || isAdmin || isSuperAdmin) return;
		supabase
			.from('profiles')
			.select('first_name, last_name, email')
			.eq('role', 'admin')
			.then(({ data }) => {
				if (data) setOrgAdmins(data as OrgAdmin[]);
			});
	}, [subscriptionIssue, isAdmin, isSuperAdmin]);

	// Do not block super admins
	if (isSuperAdmin) return null;

	// Do not block if there is no issue
	if (!subscriptionIssue) return null;

	// During grace period, only show the banner (handled by SubscriptionGracePeriodBanner)
	// subscription_inactive is always a hard block — no grace period
	if (isInGracePeriod && subscriptionIssue !== 'subscription_inactive') return null;

	// Do not block on admin pages
	if (location.pathname.startsWith("/admin")) return null;

	const issueKey = subscriptionIssue && ["payment_overdue", "payment_method_invalid", "subscription_canceled", "subscription_inactive"].includes(subscriptionIssue)
		? subscriptionIssue
		: "default";

	const message = {
		title: t(`billing.subscriptionBlocker.${issueKey}.title`),
		description: t(`billing.subscriptionBlocker.${issueKey}.description`),
	};

	return (
		<div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm">
			<div className="bg-white dark:bg-gray-900 rounded-lg shadow-2xl max-w-md w-full mx-4 p-8 flex flex-col items-center text-center gap-6">
				<div className="bg-red-100 dark:bg-red-900/30 p-4 rounded-full">
					<AlertTriangle className="h-10 w-10 text-red-600 dark:text-red-400" />
				</div>

				<div className="space-y-2">
					<h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
						{message.title}
					</h2>
					<p className="text-sm text-gray-600 dark:text-gray-400">
						{message.description}
					</p>
				</div>

				{isAdmin ? (
					<div className="flex flex-col gap-3 w-full">
						<Button
							className="w-full bg-digger-blue hover:bg-digger-blue-light flex items-center gap-2"
							onClick={() => navigate("/admin?page=billing")}
						>
							<CreditCard className="h-4 w-4" />
							{t("billing.goToBilling")}
						</Button>
						<Button
							variant="outline"
							className="w-full"
							onClick={() => window.location.href = "mailto:billing@digger.be"}
						>
							{t("billing.contactSupport")}
						</Button>
					</div>
				) : (
					<div className="flex flex-col gap-3 w-full">
						<p className="text-sm text-gray-500 dark:text-gray-400">
							{t("billing.reachOutToAdmin")}
						</p>
						{orgAdmins.length > 0 && (
							<div className="text-left border border-gray-200 dark:border-gray-700 rounded-md divide-y divide-gray-100 dark:divide-gray-700">
								{orgAdmins.map((admin, i) => (
									<div key={i} className="px-4 py-2">
										<p className="text-sm font-medium text-gray-800 dark:text-gray-200">
											{[admin.first_name, admin.last_name].filter(Boolean).join(" ") || t("billing.administrator")}
										</p>
										{admin.email && (
											<a
												href={`mailto:${admin.email}`}
												className="text-xs text-digger-blue hover:underline"
											>
												{admin.email}
											</a>
										)}
									</div>
								))}
							</div>
						)}
					</div>
				)}
			</div>
		</div>
	);
};
